import { z } from 'zod';
import { versionedKindSchema } from './versioning.js';

/**
 * The PLAN of the referents the save box offers to update (vitrine.md §3.3,
 * H22, H23; planche `references.html`, section 3).
 *
 * Pinning has a cost: a new version of a criterion changes nothing while the
 * protocols that pin the old one have no version of their own, and a new
 * version of a protocol plays nowhere until the experiences that pin it have
 * one. So when « Enregistrer une nouvelle version » is about to write the v7 of
 * an object, the box asks the server who is behind, and shows it:
 *
 * - `pinning` - the referents whose LATEST DEV version pins a precise version
 *   of the object. Each can be given a new version that takes the v7, a
 *   checkbox per line;
 * - `following` - the referents whose latest dev version cites it at `latest`,
 *   which see the v7 by themselves and are only named;
 * - `second` and `secondFollowing` - the same, one hop further, and only for a
 *   criterion: the EXPERIENCES that pin (or follow) the protocols of `pinning`.
 *   Ticking one of them needs its protocol ticked, because its new version
 *   takes the NEW version of that protocol, which only exists if the protocol
 *   gets one. The walk stops at the experiences (H23);
 * - `links` - the active links that pin an experience of the plan. A link is
 *   not versioned: it is named so the operator knows it stays on the old
 *   version, and changes it in Réglages > Partage.
 *
 * It WRITES NOTHING: it is the read the box draws itself from, and the save
 * reads it again before it writes, so a referent that moved in between is
 * judged on the base as it is then.
 */

/** A referent that cites the object at `latest`: it follows, and nothing is written for it. */
export const referentFollowerSchema = z
  .object({
    kind: versionedKindSchema,
    id: z.string().min(1),
    name: z.string().min(1),
    /** The number of the referent's latest dev version - the one that cites. */
    versionNumber: z.number().int().positive(),
  })
  .strict();
export type ReferentFollower = z.infer<typeof referentFollowerSchema>;

/**
 * A referent that pins a precise version of the object.
 *
 * `draft` is the server draft the referent holds, if any (H22): the new version
 * is made from the latest dev version and the draft stays where it is, and the
 * box says whose it is. `pins` are the versions of the object that this
 * referent's latest dev version pins, distinct and ascending - one referent can
 * pin the same object in several places.
 */
export const referentPinnerSchema = referentFollowerSchema
  .extend({
    /** The number the referent's new version will take. */
    nextNumber: z.number().int().positive(),
    pins: z.array(z.number().int().positive()).min(1),
    draft: z
      .object({
        updatedAt: z.string().datetime(),
        updatedBy: z.string().min(1),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ReferentPinner = z.infer<typeof referentPinnerSchema>;

/** A referent of the second hop, with the referent of the first it pins. */
export const referentSecondPinnerSchema = referentPinnerSchema
  .extend({
    via: z
      .object({ kind: versionedKindSchema, id: z.string().min(1), name: z.string().min(1) })
      .strict(),
  })
  .strict();
export type ReferentSecondPinner = z.infer<typeof referentSecondPinnerSchema>;

/** An active link that pins an experience of the plan - named, never updated (H23). */
export const referentLinkSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    experienceId: z.string().min(1),
    experienceName: z.string().min(1),
    pin: z.number().int().positive(),
  })
  .strict();
export type ReferentLink = z.infer<typeof referentLinkSchema>;

export const referentPlanSchema = z
  .object({
    /** The number the version being saved will take. */
    nextNumber: z.number().int().positive(),
    pinning: z.array(referentPinnerSchema),
    following: z.array(referentFollowerSchema),
    second: z.array(referentSecondPinnerSchema),
    secondFollowing: z.array(referentFollowerSchema),
    links: z.array(referentLinkSchema),
  })
  .strict();
export type ReferentPlan = z.infer<typeof referentPlanSchema>;

/** What the box shows for an object nothing cites yet: the empty plan. */
export function emptyReferentPlan(nextNumber: number): ReferentPlan {
  return { nextNumber, pinning: [], following: [], second: [], secondFollowing: [], links: [] };
}
